import logger from '../services/loggerService';

export enum CircuitState {
    CLOSED = 'CLOSED', // Normal operation
    OPEN = 'OPEN',     // Failing, reject immediately
    HALF_OPEN = 'HALF_OPEN' // Testing if service is back
}

export class CircuitBreakerError extends Error {
    public readonly code = 'CIRCUIT_OPEN';
    constructor(message: string) {
        super(message);
        this.name = 'CircuitBreakerError';
        Object.setPrototypeOf(this, CircuitBreakerError.prototype);
    }
}

export class CircuitBreaker {
    private state: CircuitState = CircuitState.CLOSED;
    private failures: number = 0;
    private lastFailureTime: number = 0;
    private trialInProgress: boolean = false;
    private trialStartTime?: number;
    private readonly threshold: number;
    private readonly resetTimeout: number;
    private readonly isFailure: (error: unknown) => boolean;
    private readonly storageKey?: string;
    private readonly halfOpenTimeoutMs: number;

    constructor(
        threshold: number = 5,
        resetTimeout: number = 30000,
        isFailure: (error: unknown) => boolean = () => true,
        storageKey?: string,
        halfOpenTimeoutMs: number = 10000
    ) {
        this.threshold = threshold;
        this.resetTimeout = resetTimeout;
        this.isFailure = isFailure;
        this.storageKey = storageKey ? `sp:ui:cb:${storageKey}` : undefined;
        this.halfOpenTimeoutMs = halfOpenTimeoutMs;
        this.loadState();
    }

    private loadState(): void {
        if (!this.storageKey || typeof window === 'undefined' || !window.sessionStorage) {
            return;
        }
        try {
            const raw = window.sessionStorage.getItem(this.storageKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                const ONE_HOUR = 3600000; // TTL Validation
                if (parsed && typeof parsed === 'object') {
                    const updatedAt = parsed.updatedAt || 0;
                    if (Date.now() - updatedAt < ONE_HOUR) {
                        if (parsed.state in CircuitState) {
                            this.state = parsed.state as CircuitState;
                        }
                        if (typeof parsed.failures === 'number') {
                            this.failures = parsed.failures;
                        }
                        if (typeof parsed.lastFailureTime === 'number') {
                            this.lastFailureTime = parsed.lastFailureTime;
                        }
                        logger.info(`Circuit Breaker state loaded for ${this.storageKey}: ${this.state} (failures: ${this.failures})`);
                    } else {
                        logger.info(`Circuit Breaker cached state for ${this.storageKey} expired, resetting.`);
                        this.clearState();
                    }
                }
            }
        } catch (error) {
            logger.warn(`Failed to load Circuit Breaker state for ${this.storageKey}`, { error });
        }
    }

    private saveState(): void {
        if (!this.storageKey || typeof window === 'undefined' || !window.sessionStorage) {
            return;
        }
        try {
            const data = {
                state: this.state,
                failures: this.failures,
                lastFailureTime: this.lastFailureTime,
                updatedAt: Date.now()
            };
            window.sessionStorage.setItem(this.storageKey, JSON.stringify(data));
        } catch (error) {
            logger.warn(`Failed to save Circuit Breaker state for ${this.storageKey}`, { error });
        }
    }

    private clearState(): void {
        if (this.storageKey && typeof window !== 'undefined' && window.sessionStorage) {
            try {
                window.sessionStorage.removeItem(this.storageKey);
            } catch (error) {
                logger.warn(`Failed to clear Circuit Breaker state for ${this.storageKey}`, { error });
            }
        }
        this.state = CircuitState.CLOSED;
        this.failures = 0;
        this.lastFailureTime = 0;
    }

    /**
     * Executes an action through the circuit breaker.
     * 
     * DESIGN RATIONALE:
     * - Protects backend from failure cascades.
     * - SECURITY FIRST: Rejects immediately when OPEN to prevent resource exhaustion.
     * - CONCURRENCY AWARE: Only allows one trial request during HALF_OPEN to safely test recovery.
     */
    public async execute<T>(action: () => Promise<T>): Promise<T> {
        if (this.state === CircuitState.OPEN) {
            if (Date.now() - this.lastFailureTime > this.resetTimeout) {
                this.transition(CircuitState.HALF_OPEN);
            } else {
                throw new CircuitBreakerError('Circuit is OPEN: Service Unavailable');
            }
        }

        // CONCURRENCY GUARD: In HALF_OPEN, only allow exactly one experiment
        if (this.state === CircuitState.HALF_OPEN) {
            if (this.trialInProgress) {
                // If a trial is in progress, check if it has timed out
                const startTime = this.trialStartTime || 0;
                if (Date.now() - startTime > this.halfOpenTimeoutMs) {
                    logger.warn('Circuit Breaker: HALF_OPEN trial timed out. Resetting trial guard.');
                    this.trialInProgress = false;
                } else {
                    throw new CircuitBreakerError('Circuit is HALF_OPEN: Trial already in progress');
                }
            }
            this.trialInProgress = true;
            this.trialStartTime = Date.now();
        }

        try {
            const result = await action();
            // Successful trial!
            if (this.state === CircuitState.HALF_OPEN) {
                this.reset();
            }
            return result;
        } catch (error) {
            if (this.isFailure(error)) {
                this.recordFailure();
            } else if (this.state === CircuitState.HALF_OPEN) {
                // A non-system error (e.g., 400 Bad Request) means the service is reachable.
                // We should reset the circuit because the service is actually up!
                this.reset();
            }
            throw error;
        } finally {
            this.trialInProgress = false;
            this.trialStartTime = undefined;
        }
    }

    private recordFailure(): void {
        this.failures++;
        this.lastFailureTime = Date.now();

        if (this.state === CircuitState.HALF_OPEN || this.failures >= this.threshold) {
            this.transition(CircuitState.OPEN);
        } else {
            this.saveState();
        }
    }

    private reset(): void {
        this.failures = 0;
        this.state = CircuitState.CLOSED;
        this.trialInProgress = false;
        this.trialStartTime = undefined;
        this.saveState();
        logger.info('Circuit Breaker RESET: Service is back online');
    }

    private transition(newState: CircuitState): void {
        this.state = newState;
        this.saveState();
        logger.warn(`Circuit Breaker State Changed to: ${newState}`);
    }
}
